/**
 * Turn-by-turn navigation — always in-app (Bolt/InDrive style).
 * - Android: Kotlin RiderNavigationActivity or Capacitor Navigation SDK
 * - iOS: Capacitor Navigation SDK
 * - Web: caller shows InAppNavigationView (never opens external Google Maps)
 */
import { Geolocation } from '@capacitor/geolocation';
import { isAndroidApp, isCapacitorNative, isIosApp } from '../utils/nativePlatform';
import { Go7RiderNavigation } from './go7RiderNavigation';

export type TravelMode = 'TWO_WHEELER' | 'DRIVING' | 'WALKING' | 'CYCLING';

export type NavigationMode = 'native-sdk' | 'in-app-web' | 'unavailable';

const TRAVEL_MODE_MAP: Record<string, TravelMode> = {
  TWO_WHEELER: 'TWO_WHEELER',
  DRIVING: 'DRIVING',
  WALKING: 'WALKING',
  CYCLING: 'CYCLING',
};

let navModulePromise: Promise<any> | null = null;

async function getGoogleNavigationPlugin() {
  if (!isCapacitorNative()) return null;
  if (!navModulePromise) {
    navModulePromise = import('capacitor-google-navigation')
      .then((m) => m.GoogleNavigation)
      .catch(() => null);
  }
  return navModulePromise;
}

async function ensureLocationPermission(): Promise<boolean> {
  if (!isCapacitorNative()) {
    if (!('geolocation' in navigator)) return false;
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        () => resolve(true),
        () => resolve(false),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
  }
  const status = await Geolocation.checkPermissions();
  if (status.location === 'granted') return true;
  const req = await Geolocation.requestPermissions();
  return req.location === 'granted';
}

export function fleetTypeToTravelMode(fleetType: 'rider' | 'bicycle' | 'leg-fleet'): TravelMode {
  if (fleetType === 'leg-fleet') return 'WALKING';
  if (fleetType === 'bicycle') return 'CYCLING';
  return 'TWO_WHEELER';
}

/** Web/PWA — navigation stays inside the app UI */
export function shouldUseInAppWebNavigation(): boolean {
  return !isCapacitorNative();
}

export async function startNativeNavigation(options: {
  lat: number;
  lng: number;
  title?: string;
  travelMode?: TravelMode | string;
  origin?: { lat: number; lng: number };
}): Promise<{ ok: boolean; mode: NavigationMode }> {
  const { lat, lng, title, travelMode = 'TWO_WHEELER' } = options;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return { ok: false, mode: 'unavailable' };
  }

  const travel = TRAVEL_MODE_MAP[travelMode] || 'TWO_WHEELER';

  // Stay inside the rider WebView / browser — never open a separate native Activity
  if (shouldUseInAppWebNavigation() || isCapacitorNative()) {
    const hasPermission = await ensureLocationPermission();
    if (!hasPermission) {
      return { ok: false, mode: 'unavailable' };
    }
    return { ok: true, mode: 'in-app-web' };
  }

  // Android — Kotlin full-screen Navigation SDK (legacy fallback)
  if (isAndroidApp()) {
    try {
      const hasPermission = await ensureLocationPermission();
      if (!hasPermission) return { ok: false, mode: 'unavailable' };

      await Go7RiderNavigation.openNavigation({ lat, lng, title });
      return { ok: true, mode: 'native-sdk' };
    } catch (err) {
      console.warn('[nav] Kotlin navigation failed, trying Capacitor plugin:', err);
    }
  }

  // Capacitor Google Navigation SDK overlay (in-app)
  const GoogleNavigation = await getGoogleNavigationPlugin();
  if (GoogleNavigation && (isAndroidApp() || isIosApp())) {
    try {
      const hasPermission = await ensureLocationPermission();
      if (!hasPermission) return { ok: false, mode: 'unavailable' };

      await GoogleNavigation.initialize({});
      await GoogleNavigation.showNavigationView({ show: true });
      await GoogleNavigation.startNavigation({
        destinationLatitude: lat,
        destinationLongitude: lng,
        travelMode: travel,
      });

      return { ok: true, mode: 'native-sdk' };
    } catch (err) {
      console.warn('[nav] Capacitor Navigation SDK failed:', err);
      try {
        await GoogleNavigation?.showNavigationView?.({ show: false });
      } catch {
        /* ignore */
      }
    }
  }

  return { ok: false, mode: 'unavailable' };
}

export async function stopNativeNavigation(): Promise<void> {
  const GoogleNavigation = await getGoogleNavigationPlugin();
  if (!GoogleNavigation) return;
  try {
    await GoogleNavigation.stopNavigation();
    await GoogleNavigation.showNavigationView({ show: false });
  } catch {
    /* ignore */
  }
}

export async function isNativeNavigationAvailable(): Promise<boolean> {
  if (shouldUseInAppWebNavigation()) return true;
  if (isAndroidApp()) return true;
  const plugin = await getGoogleNavigationPlugin();
  return Boolean(plugin);
}
